import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, filter, take, takeUntil, tap } from 'rxjs';

import { DeviceEventsService } from '@app/api/device/devices-events.service';
import { DeviceSosRequestEventDto } from '@app/api/device/dtos';
import { TabManagementService } from '@app/core/tab-management';
import { getDeviceName } from '@app/features/device/pipes/device-name.pipe';
import { DeviceStoreService } from '@app/store/device/device-store.service';

import { AlarmSosDialogComponent } from './alarm-sos-dialog.component';
import { AlarmSosDialogNotificationService } from './alarm-sos-dialog-notification.service';

const ALARM_DIALOG_CLOSED_MESSAGE_TYPE = 'alarm-sos-dialog-closed';
interface AlarmDialogClosedMessage {
  tag: string;
}

@Injectable({ providedIn: 'root' })
export class AlarmSosDialogListener {
  readonly notificationGranted$ = new BehaviorSubject(false);

  private readonly dialog = inject(MatDialog);

  private readonly deviceEventsService = inject(DeviceEventsService);

  private readonly deviceStore = inject(DeviceStoreService);

  private readonly notificationService = inject(AlarmSosDialogNotificationService);

  private readonly tabManagementService = inject(TabManagementService);

  public init(): void {
    this.deviceEventsService.sosRequest$.pipe(tap((message) => this.openDialog(message))).subscribe();

    if ('Notification' in window) {
      Notification.requestPermission().then((res) => {
        this.notificationGranted$.next(res === 'granted');
      });
    }
  }

  protected openDialog(message: DeviceSosRequestEventDto): void {
    if (this.tabManagementService.isActive) {
      this.notificationService.play();
    }

    const device = this.deviceStore.getDeviceById(message.deviceId);
    let notification: Notification | null = null;
    const tag = `ds-sos-${message.deviceId}_${message.timestamp}`;

    if (this.notificationGranted$.value) {
      notification = new Notification('SOS', {
        body: `Получен SOS сигнал от устройства ${device ? getDeviceName(device) : message.deviceId}`,
        tag,
      });
    }

    const dialog = AlarmSosDialogComponent.open(this.dialog, {
      deviceId: message.deviceId,
      deviceName: device?.name,
    });

    const dialogClosed$ = dialog.afterClosed();
    let closedByMessage = false;

    dialog.componentInstance.interaction$
      .pipe(
        take(1),
        tap(() =>
          this.tabManagementService.broadcastData<AlarmDialogClosedMessage>(ALARM_DIALOG_CLOSED_MESSAGE_TYPE, { tag }),
        ),
        takeUntil(dialogClosed$),
      )
      .subscribe();

    this.tabManagementService
      .listenData<AlarmDialogClosedMessage>(ALARM_DIALOG_CLOSED_MESSAGE_TYPE)
      .pipe(
        filter((data) => data.tag === tag),
        tap(() => {
          this.notificationService.stop();
          closedByMessage = true;
          dialog.close();
        }),
        take(1),
        takeUntil(dialogClosed$),
      )
      .subscribe();

    dialogClosed$
      .pipe(
        take(1),
        tap(() => {
          notification?.close();

          if (!closedByMessage) {
            this.tabManagementService.broadcastData<AlarmDialogClosedMessage>(ALARM_DIALOG_CLOSED_MESSAGE_TYPE, {
              tag,
            });
          }
        }),
      )
      .subscribe();
  }
}
